import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, CalendarDays, Users, Phone, Check, X } from "lucide-react";

const STATUS = {
  requested: { label: "Förfrågan", cls: "bg-[#F59E0B]/15 text-[#F59E0B]" },
  confirmed: { label: "Bekräftad", cls: "bg-[#3B82F6]/15 text-[#93C5FD]" },
  seated: { label: "Ankommit", cls: "bg-[#A855F7]/15 text-[#D8B4FE]" },
  completed: { label: "Avslutad", cls: "bg-[#10B981]/15 text-[#10B981]" },
  cancelled: { label: "Avbokad", cls: "bg-[#EF4444]/15 text-[#FCA5A5]" },
  no_show: { label: "Uteblev", cls: "bg-[#6B7280]/20 text-[#A8ACB4]" },
};
const SOURCES = [["phone", "Telefon"], ["walk-in", "På plats"], ["website", "Hemsidan"], ["instagram", "Instagram"], ["facebook", "Facebook"]];
const today = () => new Date().toLocaleDateString("sv-SE");
const EMPTY = { customer_name: "", customer_phone: "", party_size: "2", date: today(), time: "18:00", source: "phone", notes: "" };

export default function BookingsPanel() {
  const [date, setDate] = useState(today());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/admin/bookings?date=${date}`); setBookings(res.data.bookings || []); }
    catch { toast.error("Kunde inte ladda bokningar"); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/bookings", { ...form, party_size: parseInt(form.party_size) || 2 });
      toast.success("Bokning skapad");
      setDialog(false); setForm({ ...EMPTY, date });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte skapa bokningen"); }
    finally { setSaving(false); }
  };

  const setStatus = async (b, status, okMsg) => {
    setBusyId(b.id);
    try { await api.put(`/admin/bookings/${b.id}/status`, { status }); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Misslyckades"); }
    finally { setBusyId(null); }
  };

  const active = bookings.filter(b => !["cancelled", "no_show", "completed"].includes(b.status));
  const done = bookings.filter(b => ["cancelled", "no_show", "completed"].includes(b.status));
  const totalGuests = active.reduce((s, b) => s + (b.party_size || 0), 0);

  const row = (b) => (
    <div key={b.id} className="bg-[#121212] rounded-xl border border-[#27272A] p-4 flex items-start gap-3 flex-wrap">
      <div className="w-14 text-center shrink-0">
        <p className="text-lg font-bold text-[#D4B483]">{b.time}</p>
        <p className="text-[10px] text-[#9A9AA2] flex items-center justify-center gap-0.5"><Users className="w-3 h-3" />{b.party_size}</p>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-medium">
          {b.customer_name}
          <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS[b.status]?.cls}`}>{STATUS[b.status]?.label}</span>
          {b.status === "requested" && b.approval_required && (
            <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#EF4444]/15 text-[#FCA5A5]">Kräver godkännande</span>
          )}
          {b.quote_id && <span className="ml-2 text-[10px] text-[#9A9AA2]">från offert</span>}
        </p>
        <p className="text-[11px] text-[#9A9AA2] mt-0.5">
          {b.customer_phone && <span className="inline-flex items-center gap-1 mr-2"><Phone className="w-3 h-3" />{b.customer_phone}</span>}
          via {SOURCES.find(s => s[0] === b.source)?.[1] || b.source} · {b.created_by}
        </p>
        {b.notes && <p className="text-[11px] text-[#C8C8CE] mt-1">{b.notes}</p>}
      </div>
      <div className="flex gap-1.5 shrink-0">
        {b.status === "requested" && (
          <>
            <Button size="sm" disabled={busyId === b.id} onClick={() => setStatus(b, "confirmed", "Bokningen bekräftad")}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-8 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Bekräfta</Button>
            <Button size="sm" variant="outline" disabled={busyId === b.id} onClick={() => window.confirm("Avboka bokningen?") && setStatus(b, "cancelled", "Avbokad")}
              className="border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/10 h-8 text-xs"><X className="w-3.5 h-3.5" /></Button>
          </>
        )}
        {b.status === "confirmed" && (
          <>
            <Button size="sm" disabled={busyId === b.id} onClick={() => setStatus(b, "seated", "Gästen har ankommit")}
              className="bg-[#A855F7] hover:bg-[#9333EA] text-white h-8 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Ankommit</Button>
            <Button size="sm" variant="outline" disabled={busyId === b.id} onClick={() => setStatus(b, "no_show", "Markerad som utebliven")}
              className="border-[#6B7280]/40 text-[#A8ACB4] hover:bg-[#6B7280]/10 h-8 text-xs">Uteblev</Button>
            <Button size="sm" variant="outline" disabled={busyId === b.id} onClick={() => window.confirm("Avboka bokningen?") && setStatus(b, "cancelled", "Avbokad")}
              className="border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/10 h-8 text-xs"><X className="w-3.5 h-3.5" /></Button>
          </>
        )}
        {b.status === "seated" && (
          <Button size="sm" disabled={busyId === b.id} onClick={() => setStatus(b, "completed", "Bokningen avslutad")}
            className="bg-[#10B981] hover:bg-[#059669] text-white h-8 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Avsluta</Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl" data-testid="bookings-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Bokningar</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">Bordsbokningar via telefon, på plats eller sociala medier.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] w-40 h-9" />
          <Button onClick={() => { setForm({ ...EMPTY, date }); setDialog(true); }}
            className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Ny bokning
          </Button>
        </div>
      </div>

      {active.length > 0 && (
        <p className="text-xs text-[#C0C0C6] mb-3">{active.length} bokningar · {totalGuests} gäster denna dag</p>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : bookings.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <CalendarDays className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga bokningar {date === today() ? "idag" : date}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {active.map(row)}
          {done.length > 0 && (
            <>
              <p className="text-[11px] uppercase tracking-wider text-[#9A9AA2] pt-3">Avslutade</p>
              <div className="opacity-60 space-y-2">{done.map(row)}</div>
            </>
          )}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-md">
          <DialogHeader><DialogTitle className="text-white">Ny bokning</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Namn *</Label>
                <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Telefon</Label>
                <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Datum</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Tid</Label>
                <Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Antal gäster</Label>
                <Input type="number" min="1" value={form.party_size} onChange={e => setForm(f => ({ ...f, party_size: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kanal</Label>
                <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-md h-10 text-sm px-2">
                  {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Anteckning (allergi, barnstol, fönsterbord ...)</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
            </div>
            <Button onClick={create} disabled={saving || !form.customer_name.trim()}
              className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Boka
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
